import React, { useState, useRef, useCallback } from 'react';
import { portfolioAPI } from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Image,
  Upload,
  Trash2,
  X,
  Plus,
  Loader2,
  ZoomIn,
  Edit,
  Camera,
  Grid3X3,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

// Portfolio Gallery Component (for viewing)
export const PortfolioGallery = ({ items = [], workerId, isOwner = false, onRefresh }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">No Portfolio Items</h3>
        <p className="text-gray-500">
          {isOwner 
            ? "Showcase your best work by adding photos of completed projects"
            : "This worker hasn't added any portfolio items yet"
          }
        </p>
      </div>
    );
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(items[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <>
      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="portfolio-grid">
        {items.map((item, index) => (
          <div
            key={item._id}
            className="relative group aspect-square rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
            onClick={() => openLightbox(index)}
            data-testid={`portfolio-item-${item._id}`}
          >
            <img
              src={item.image_url}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-medium text-sm truncate">{item.title}</p>
                {item.category && (
                  <Badge variant="secondary" className="bg-white/20 text-white text-xs mt-1">
                    {item.category}
                  </Badge>
                )}
              </div>
              <div className="absolute top-2 right-2">
                <ZoomIn className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="portfolio-lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={closeLightbox}
          >
            <X className="h-8 w-8" />
          </button>
          
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          
          <div 
            className="max-w-4xl max-h-[80vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[currentIndex].image_url}
              alt={items[currentIndex].title}
              className="max-w-full max-h-[65vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              <h3 className="text-xl font-semibold">{items[currentIndex].title}</h3>
              {items[currentIndex].description && (
                <p className="text-gray-300 mt-2 max-w-2xl">{items[currentIndex].description}</p>
              )}
              {items[currentIndex].category && (
                <Badge className="mt-2 bg-blue-600">{items[currentIndex].category}</Badge>
              )}
              <p className="text-gray-400 text-sm mt-2">
                {currentIndex + 1} of {items.length}
              </p>
            </div>
          </div>
          
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
          >
            <ChevronRight className="h-10 w-10" />
          </button>
        </div>
      )}
    </>
  );
};

// Portfolio Manager Component (for editing)
export const PortfolioManager = ({ items = [], onRefresh }) => {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);
  
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    category: '',
    image_data: null,
    preview: null
  });

  const categories = [
    'Electrical', 'Plumbing', 'Carpentry', 'HVAC', 'Roofing',
    'Painting', 'Masonry', 'Drywall', 'Flooring', 'Welding',
    'Renovation', 'New Construction', 'Repair', 'Other'
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File',
        description: 'Please select an image file (JPEG, PNG, etc.)',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Image must be less than 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Read and convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewItem(prev => ({
        ...prev,
        image_data: e.target.result,
        preview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!newItem.title.trim()) {
      toast({
        title: 'Title Required',
        description: 'Please enter a title for your portfolio item',
        variant: 'destructive'
      });
      return;
    }

    if (!newItem.image_data) {
      toast({
        title: 'Image Required',
        description: 'Please select an image to upload',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    try {
      await portfolioAPI.addItem({
        title: newItem.title.trim(),
        description: newItem.description.trim() || null,
        category: newItem.category || null,
        image_data: newItem.image_data
      });

      toast({
        title: 'Success',
        description: 'Portfolio item added successfully'
      });

      // Reset form
      setNewItem({
        title: '',
        description: '',
        category: '',
        image_data: null,
        preview: null
      });
      setShowUploadForm(false);
      
      // Refresh portfolio
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error uploading portfolio item:', error);
      toast({
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload portfolio item',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) {
      return;
    }

    setDeleting(itemId);
    try {
      await portfolioAPI.deleteItem(itemId);
      toast({
        title: 'Deleted',
        description: 'Portfolio item removed successfully'
      });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting portfolio item:', error);
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete portfolio item',
        variant: 'destructive'
      });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="portfolio-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Your Portfolio</h2>
          <p className="text-gray-500 text-sm">Showcase your best work to attract more clients</p>
        </div>
        <Button 
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-portfolio-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Photo
        </Button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-testid="upload-form">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Image Upload */}
            <div>
              <Label className="mb-2 block">Project Photo *</Label>
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                  ${newItem.preview ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-100'}`}
                onClick={() => fileInputRef.current?.click()}
              >
                {newItem.preview ? (
                  <div className="relative">
                    <img
                      src={newItem.preview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg object-contain"
                    />
                    <button
                      className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewItem(prev => ({ ...prev, image_data: null, preview: null }));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Click to upload image</p>
                    <p className="text-gray-400 text-sm mt-1">JPEG, PNG up to 5MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Kitchen Renovation Project"
                  value={newItem.title}
                  onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="portfolio-title-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  value={newItem.category}
                  onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="portfolio-category-select"
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the project, your role, and the outcome..."
                  value={newItem.description}
                  onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  data-testid="portfolio-description-input"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadForm(false);
                setNewItem({ title: '', description: '', category: '', image_data: null, preview: null });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !newItem.title || !newItem.image_data}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="upload-portfolio-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Portfolio Grid */}
      {items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Image className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No Portfolio Items Yet</h3>
          <p className="text-gray-500 mb-4">Upload photos of your completed projects to showcase your work</p>
          <Button 
            onClick={() => setShowUploadForm(true)}
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Photo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="portfolio-items-grid">
          {items.map((item) => (
            <div
              key={item._id}
              className="relative group aspect-square rounded-xl overflow-hidden shadow-md"
              data-testid={`portfolio-manage-item-${item._id}`}
            >
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(item._id)}
                  disabled={deleting === item._id}
                  data-testid={`delete-portfolio-${item._id}`}
                >
                  {deleting === item._id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white text-sm font-medium truncate">{item.title}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default { PortfolioGallery, PortfolioManager };
